'use client'
import { useState, useRef, useEffect } from "react"
// import { ChevronDown, Calendar } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/chart/select"
import { Card, CardContent } from "../ui/chart/card"
// import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/chart/drop"
import { line_Chart } from "../../lib/apis"
import axios from "axios"
import { useLocation, useNavigate } from "react-router-dom"
import Loader from "../loader"
import { useTranslation } from "react-i18next"
import { FaArrowLeft } from "react-icons/fa"
import Breadcrumb from "../Breadcrumb"
import { scrollToTop } from "../../lib/utils"


interface PriceDataItem {
  month: string
  price: number
  date: string
}
type Product = {
  product_id: number;
  product_name: string;
};

type Arrondissement = {
  arrondissement_id: number;
  arrondissement_name: string;
};

type Uml = {
  uml_name: string;
};

type Market = {
  market_id: number;
  market_name: string;
};

interface LineChartByPrice {
  priceData: PriceDataItem[];
  products: Product[];
  arrondissements: Arrondissement[];
  umls: Uml[]
  markets: Market[]
  selected_uml: string
}
// Fonction pour formater les prix en FCFA
const formatPrice = (price: number): string => {
  return new Intl.NumberFormat("fr-FR").format(price) + " FCFA"
}
type PricePoint = {
  month: string;
  price: number;
  date: string;
};

export default function PriceAnalysis() {
  const { t, i18n } = useTranslation()
  const { state } = useLocation()
  const navigate = useNavigate()
  const parsedProduct = state?.product

  const [product, setProduct] = useState(i18n.language === 'fr' ? parsedProduct?.product_name : parsedProduct?.product_name_en)
  const [productId, setProductId] = useState(parsedProduct?.product_id)
  const [marketId, setMarketId] = useState(parsedProduct?.market_id);
  const [arrondissementId, setArrondissementId] = useState(parsedProduct?.arrondissement_id);
  const [location1, setLocation1] = useState(i18n.language === 'fr' ? parsedProduct?.arrondissement_name : parsedProduct?.arrondissement_name_en)
  const [location2, setLocation2] = useState(i18n.language === 'fr' ? parsedProduct?.market_name : parsedProduct?.market_name_en)
  const [packageType, setPackageType] = useState(i18n.language === 'fr' ? parsedProduct?.collect_type : parsedProduct?.collect_type_en)
  const [activePoint, setActivePoint] = useState(1) // Index du point actif (février par défaut)
  const [showTooltip, setShowTooltip] = useState(true)
  const svgRef = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState(false)
  const token = localStorage.getItem("token")
  const [lineChartbyPrice, setLineChartbyPrice] = useState<LineChartByPrice | null>(null);

  useEffect(() => {
    scrollToTop()
  }, [])


  const fetchlineChart = async (
    ProductId: number,
    marketId: number,
    PackageType?: string,
    ArrondissementId?: number,
  ) => {
    setLoading(true);
    try {
      let url = `${line_Chart}?product_id=${ProductId}`;
      if (PackageType) {
        url += `&collect_type=${PackageType}`
      }
      if (ArrondissementId) {
        url += `&arrondissement_id=${ArrondissementId}`
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (i18n.language === 'fr') {
        setLineChartbyPrice(response.data.data_fr);
        setPackageType(response.data.data_fr.selected_uml)
        setLocation1(response.data.data_fr.selected_arrondissement?.arrondissement_name)
        setLocation2(response.data.data_fr.selected_market?.market_name)
        setProduct(response?.data?.data_fr?.selected_product?.product_name)
      } else {
        setLineChartbyPrice(response.data.data_en);
        setPackageType(response.data.data_en.selected_uml)
        setLocation1(response.data.data_en.selected_arrondissement?.arrondissement_name)
        setLocation2(response.data.data_en.selected_market?.market_name)
        setProduct(response?.data?.data_en?.selected_product?.product_name)
      }

    } catch (error) {
      console.error("Erreur lors de la récupération du graphique:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchlineChart(productId, marketId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, marketId, i18n.language]);

  const priceData = lineChartbyPrice?.priceData;
  const chartHeight = 200; // px
  const maxPriceValue = priceData && Math.max(...priceData.map(m => Number(m.price)));
  const getYPosition = maxPriceValue ? (price: number) => {
    return (price / maxPriceValue) * chartHeight;
  } : () => 0; // Si maxPriceValue est 0, retourner 0 pour éviter la division par zéro

  // Calcul des positions des points sur le graphique
  const points = priceData
    ? priceData.map((data: PricePoint, index: number) => {
      const chartWidth = 1000;
      const horizontalPadding = 35;
      const usableWidth = chartWidth - horizontalPadding * 2;
      const x = priceData.length === 1
        ? chartWidth / 2
        : horizontalPadding + (index * usableWidth) / (priceData.length - 1);
      const y = 275 - getYPosition(Number(data.price));
      return { x, y, ...data };
    })
    : [];

  // Générer le chemin SVG pour la ligne du graphique
  const pathD = points && points.map(({ x, y }, i) => (i === 0 ? "M" : "L") + x + "," + y).join(" ");

  // Calculer le prix moyen, max et min
  const avgPrice = Math.round(points.reduce((sum, p) => sum + p?.price, 0) / points?.length)
  const maxPrice = Math.max(...points.map((p: any) => p?.price))
  const minPrice = Math.min(...points.map((p: any) => p?.price))


  // Prix actif (sélectionné ou survolé)
  const activeData = points && points[activePoint]

  // Mettre à jour la position de l'infobulle lors du survol
  const handlePointHover = (index: number): void => {
    setActivePoint(index)
    setShowTooltip(true)
  }

  const allProducts = lineChartbyPrice?.products || []
  const allArrondissements = lineChartbyPrice?.arrondissements || []
  const allUmls = lineChartbyPrice?.umls || []
  const allMarkets = lineChartbyPrice?.markets || []

  const getProductIdByName = (name: string) => {
    return allProducts.find(p => p.product_name === name)?.product_id;
  };

  const getArrondissementIdByName = (name: string) => {
    return allArrondissements.find(a => a.arrondissement_name === name)?.arrondissement_id;
  };
  const getMarketIdByName = (name: string) => {
    return allMarkets.find(a => a.market_name === name)?.market_id;
  };
  const handleProductChange = (newProduct: string) => {
    setProduct(newProduct);
    const id = getProductIdByName(newProduct);
    if (id !== undefined) {
      setProductId(id);
      fetchlineChart(id, marketId);
    }
  };

  const handleMarketChange = (newMarket: string) => {
    setLocation2(newMarket);
    const id = getMarketIdByName(newMarket);
    if (id !== undefined) {
      setMarketId(id);
      fetchlineChart(productId, id, packageType, arrondissementId);
    }
  };

  const handleArrondissementChange = async (newArrondissement: string) => {
    setLocation1(newArrondissement);
    const id = getArrondissementIdByName(newArrondissement);

    if (id !== undefined) {
      setArrondissementId(id);
      fetchlineChart(productId, id, packageType, arrondissementId);
    }
  };

  const handlePackageTypeChange = (newPackageType: string) => {
    setPackageType(newPackageType);
    fetchlineChart(productId, marketId, newPackageType, arrondissementId);
  };

  function generateYAxisLabels(max: number, steps: number = 5): number[] {
    const interval = Math.ceil(max / steps);
    const labels = [];
    // labels.push(max+interval)
    for (let i = max; i >= 0; i -= interval) {
      labels.push(i);
    }
    return labels;
  }
  const yLabels = generateYAxisLabels(maxPrice);

  return (
    <div className="min-h-screen bg-[#FFF6F0] px-4 md:px-8">
      <div className="flex flex-row mt-5 mb-3 items-center justify-start w-full ml-5">

        <Breadcrumb />
      </div>
      {/* Header with title and filter */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <h1 className="text-xl font-bold mb-4 md:mb-0">
          {t('lastest_price')} <span className="text-green-600">{product}</span>, {t("dans_ville")}
          <span className="text-red-600">{location1}, </span>
          <span className="text-amber-500">{location2}</span>
        </h1>

        {/* <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2 bg-white rounded-lg px-4 py-2 text-sm border border-gray-200">
            <Calendar className="h-5 w-5 text-green-600" />
            <div>
              <div className="font-medium">Filtrer par periode</div>
              <div className="text-xs text-gray-500">Mois</div>
            </div>
            <ChevronDown className="h-4 w-4 ml-2" />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem>Jour</DropdownMenuItem>
            <DropdownMenuItem>Semaine</DropdownMenuItem>
            <DropdownMenuItem>Mois</DropdownMenuItem>
            <DropdownMenuItem>Année</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu> */}
      </div>

      {/* Main Card */}
      <Card className="bg-white shadow-sm mb-8 mt-8">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
            {/* Produit */}
            <Select value={product} onValueChange={handleProductChange}>
              <SelectTrigger>
                <SelectValue placeholder="Choisir un produit" />
              </SelectTrigger>
              <SelectContent>
                {allProducts.map((prod) => (
                  <SelectItem key={prod.product_id} value={prod.product_name}>
                    {prod.product_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Arrondissement */}
            <Select value={location1} onValueChange={handleArrondissementChange}>
              <SelectTrigger>
                <SelectValue placeholder={location1} />
              </SelectTrigger>
              <SelectContent>
                {allArrondissements.map((arr) => (
                  <SelectItem key={arr.arrondissement_id} value={arr.arrondissement_name}>
                    {arr.arrondissement_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Marché */}
            <Select value={location2} onValueChange={handleMarketChange}>
              <SelectTrigger>
                <SelectValue placeholder={location2} />
              </SelectTrigger>
              <SelectContent>
                {allMarkets.map((market) => (
                  <SelectItem key={market.market_id} value={market.market_name}>
                    {market.market_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Type d’emballage */}
            <Select value={packageType} onValueChange={handlePackageTypeChange}>
              <SelectTrigger>
                <SelectValue placeholder="Type d’emballage" />
              </SelectTrigger>
              <SelectContent>
                {allUmls.map((uml: any) => {
                  if (!uml || uml === '') return null;
                  return <SelectItem key={uml} value={uml}>
                    {uml}
                  </SelectItem>
                })}
              </SelectContent>
            </Select>
          </div>

          <div className="text-sm text-gray-500">{t('price')} (FCFA)</div>
          {/* Chart */}
          {loading ? <Loader /> :
            <>{lineChartbyPrice ?
              <div className="relative h-[340px]">
                {/* Y-axis labels */}
                <div className={`absolute top-[65px] h-[250px] flex flex-col justify-between  text-xs text-gray-500`}>
                  {yLabels.map((label, idx) => (
                    <div key={idx} style={{ height: `${chartHeight / (yLabels.length - 1)}px` }}>{label}</div>
                  ))}
                </div>

                {/* Chart SVG */}
                <div className="ml-14 mt-5 h-full" ref={svgRef}>
                  <svg className="h-full w-full" viewBox="0 0 1000 340" preserveAspectRatio="none">
                    {/* Horizontal grid lines */}
                    {yLabels.map((label, idx) => (
                      <line
                        key={idx}
                        x1="0"
                        y1={getYPosition(label) + 80}
                        x2="1000"
                        y2={getYPosition(label) + 80}
                        stroke="#E5E7EB"
                        strokeWidth="1"
                        style={{ margin: 10 }}
                      />
                    ))}

                    {/* Price line */}
                    <path d={pathD} fill="none" stroke="#2E8B57" strokeWidth="3" />

                    {/* Interactive points */}
                    {points && points.map((point, index) => (
                      <g key={index} onMouseEnter={() => handlePointHover(index)} style={{ cursor: "pointer" }}>
                        <circle
                          cx={point.x}
                          cy={point.y}
                          r={index === activePoint ? 6 : 4}
                          fill={index === activePoint ? "#2E8B57" : "#4CAF50"}
                          stroke={index === activePoint ? "white" : "none"}
                          strokeWidth="2"
                        />
                        {/* Transparent larger circle for easier hovering */}
                        <circle cx={point.x} cy={point.y} r="15" fill="transparent" />
                      </g>
                    ))}

                    {/* Active tooltip */}
                    {showTooltip && points && points.length > 0 && activePoint >= 0 && activePoint < points.length && (
                      <g transform={`translate(${points[activePoint].x + 30}, ${points[activePoint].y - 60})`}>
                        <rect
                          x="-65"
                          y="-15"
                          width="150"
                          height="55"
                          rx="6"
                          fill="#1F2937"
                          fillOpacity="0.95"
                          filter="drop-shadow(0px 2px 8px rgba(0,0,0,0.25))"
                        />
                        <polygon points="-30,30 10,-20 30,0" transform="translate(0, 20)" fill="#1F2937" />
                        <>
                          <text x="5" y="1" textAnchor='middle' fill="#10B981" fontWeight="bold" fontSize="14">
                            {formatPrice(points[activePoint].price)}
                          </text>
                          <text x="5" y="15" textAnchor="middle" fill="#ffffff" fontFamily="sans-serif" fontSize="10">
                            /{packageType}
                          </text>
                          <text x="15" y="30" textAnchor="middle" fill="white" fontSize="12">
                            {points[activePoint].date}
                          </text>
                        </>
                      </g>
                    )}

                    {/* Month labels share the exact x coordinate of their points. */}
                    {points.map((point, index) => (
                      <text
                        key={`month-${index}`}
                        x={point.x}
                        y="320"
                        textAnchor="middle"
                        fill={index === activePoint ? "#15803D" : "#9CA3AF"}
                        fontWeight={index === activePoint ? "600" : "400"}
                        fontSize="11"
                        fontFamily="sans-serif"
                      >
                        {point.month}
                      </text>
                    ))}
                  </svg>
                </div>
              </div> : <p className="text-2xl">{t('emptyMarket')}</p>}
            </>
          }
        </CardContent>
      </Card>

      {/* Analysis Section */}
      <div className="mb-8">
        <h2 className="text-xl font-bold mb-4">{t('analyse')}</h2>
        {loading ? <Loader /> :
          (avgPrice && minPrice ?
            <div className="bg-[#F9F5F0] p-6 rounded-lg transition-all duration-300">
              <p className="mb-2">
                {t('avg_price_sentence_annual', {
                  uml: packageType,
                  product,
                  location: location1,
                  price: formatPrice(avgPrice)
                })}
              </p>

              <p className="mb-2">
                {t('monthly_price_sentence', {
                  month: activeData?.month,
                  date: activeData?.date,
                  price: formatPrice(activeData?.price),
                  sign: activeData?.price > avgPrice ? '+' : '-',
                  difference: Math.round(((activeData?.price - avgPrice) / avgPrice) * 100)
                })}
              </p>

              <p className="mb-2">
                {t('highest_price_sentence', {
                  price: formatPrice(maxPrice),
                  difference: Math.round(((maxPrice - avgPrice) / avgPrice) * 100)
                })}
              </p>

              <p className="mb-2">
                {t('lowest_price_sentence', {
                  price: formatPrice(minPrice),
                  difference: Math.round(((avgPrice - minPrice) / avgPrice) * 100)
                })}
              </p>

              {points && activeData && activePoint >= 0 && (
                <p className="mb-0">
                  <span className="font-medium text-amber-800">{t('observed_trend_title')}</span>
                  {activeData.price > points[Math.max(0, activePoint - 1)]?.price
                    ? t('price_increase_trend')
                    : t('price_decrease_trend')}
                </p>
              )}
            </div> :
            <p>{t('noanalyse')}</p>
          )}
      </div>
    </div>
  )
}
